import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  ButtonGroup,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { TrainingQuestion } from '../../types/training';

interface QuestionProps {
  onAddQuestion: (question: TrainingQuestion) => void;
  onCancel: () => void;
}

const Question: React.FC<QuestionProps> = ({ onAddQuestion, onCancel }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [choiceA, setChoiceA] = useState('');
  const [choiceB, setChoiceB] = useState('');
  const [choiceC, setChoiceC] = useState('');
  const [choiceD, setChoiceD] = useState('');
  const [choiceE, setChoiceE] = useState('');
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [answerError, setAnswerError] = useState<string | null>(null);

  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuestion(value);
    if (value.length > 255) {
      setQuestionError('Question must be less than 255 characters');
    } else if (value.length === 0) {
      setQuestionError('Question is required');
    } else {
      setQuestionError(null);
    }
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAnswer(value);

    // Validate that the selected answer has a corresponding choice
    const choiceMap: { [key: string]: string } = {
      a: choiceA,
      b: choiceB,
      c: choiceC,
      d: choiceD,
      e: choiceE,
    };

    if (value && choiceMap[value]?.length === 0) {
      setAnswerError('Answer is not valid - corresponding choice is empty');
    } else {
      setAnswerError(null);
    }
  };

  const handleChoiceUpdate = (choice: string, value: string) => {
    // Clear answer if the selected choice becomes empty
    if (answer === choice && value.length === 0) {
      setAnswer('');
    }

    switch (choice) {
      case 'a':
        setChoiceA(value);
        break;
      case 'b':
        setChoiceB(value);
        break;
      case 'c':
        setChoiceC(value);
        break;
      case 'd':
        setChoiceD(value);
        break;
      case 'e':
        setChoiceE(value);
        break;
      default:
        break;
    }
  };

  const getAvailableChoices = () => {
    const choices = [];
    if (choiceA) choices.push({ value: 'a', label: choiceA });
    if (choiceB) choices.push({ value: 'b', label: choiceB });
    if (choiceC) choices.push({ value: 'c', label: choiceC });
    if (choiceD) choices.push({ value: 'd', label: choiceD });
    if (choiceE) choices.push({ value: 'e', label: choiceE });
    return choices;
  };

  const handleSave = () => {
    if (questionError || answerError) {
      return;
    }

    if (!question.trim()) {
      setQuestionError('Question is required');
      return;
    }

    if (!answer) {
      setAnswerError('Please select an answer');
      return;
    }

    const availableChoices = getAvailableChoices();
    if (availableChoices.length < 2) {
      setAnswerError('At least 2 choices are required');
      return;
    }

    // Save the question
    const questionData: TrainingQuestion = {
      question: question.trim(),
      answer: answer,
      order: 0, // Will be set by parent component
      selection_a: choiceA,
      selection_b: choiceB,
      selection_c: choiceC,
      selection_d: choiceD,
      selection_e: choiceE,
    };

    onAddQuestion(questionData);
  };

  const availableChoices = getAvailableChoices();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <QuizIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" color="primary">
            Add New Question
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Question"
              size="small"
              fullWidth
              multiline
              maxRows={4}
              error={!!questionError}
              helperText={questionError}
              onChange={handleQuestionChange}
              value={question}
              placeholder="Enter your question here..."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Answer Choices
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Choice A"
              size="small"
              fullWidth
              onChange={(e) => handleChoiceUpdate('a', e.target.value)}
              value={choiceA}
              placeholder="Enter choice A"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Choice B"
              size="small"
              fullWidth
              onChange={(e) => handleChoiceUpdate('b', e.target.value)}
              value={choiceB}
              placeholder="Enter choice B"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Choice C"
              size="small"
              fullWidth
              onChange={(e) => handleChoiceUpdate('c', e.target.value)}
              value={choiceC}
              placeholder="Enter choice C (optional)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Choice D"
              size="small"
              fullWidth
              onChange={(e) => handleChoiceUpdate('d', e.target.value)}
              value={choiceD}
              placeholder="Enter choice D (optional)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Choice E"
              size="small"
              fullWidth
              onChange={(e) => handleChoiceUpdate('e', e.target.value)}
              value={choiceE}
              placeholder="Enter choice E (optional)"
            />
          </Grid>

          {availableChoices.length > 0 && (
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                error={!!answerError}
                size="small"
              >
                <InputLabel id="answer-label">Correct Answer</InputLabel>
                <Select
                  labelId="answer-label"
                  id="answer-select"
                  value={answer}
                  input={<OutlinedInput label="Correct Answer" />}
                  onChange={handleAnswerChange}
                >
                  {availableChoices.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {answerError && <FormHelperText>{answerError}</FormHelperText>}
              </FormControl>
            </Grid>
          )}

          {availableChoices.length < 2 &&
            (choiceA || choiceB || choiceC || choiceD || choiceE) && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  At least 2 choices are required to create a question.
                </Alert>
              </Grid>
            )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <ButtonGroup>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={
                    !!questionError ||
                    !!answerError ||
                    availableChoices.length < 2
                  }
                >
                  Save Question
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default Question;
